import type { FinanceProposalSnapshotRow } from '@/services/financeService'
import { parseFinanceProposalSnapshot } from './financeProposalSnapshotParse'
import {
  extractPublicProposalId,
  publicProposalPreviewPath,
} from './financeProposalPublicId'
import { buildPublicPlanViews, type PublicPlanView } from '@/utils/propostaPublicScope'

export type ClosedScopeLine = {
  id: string
  descricao: string
  tipo: 'plano' | 'servico' | 'avulso'
  quantidade: number
  valorUnitario?: number
  total?: number
}

export type ClosedScopeViewModel = {
  lines: ClosedScopeLine[]
  servicosText: string | null
  plataforma: string | null
  observacoes: string | null
  proposalPublicUrl: string | null
  versao: number | null
  createdAt: string | null
}

const TIPO_LABEL: Record<ClosedScopeLine['tipo'], string> = {
  plano: 'Plano',
  servico: 'Serviço',
  avulso: 'Avulso',
}

export function closedScopeTipoLabel(tipo: ClosedScopeLine['tipo']): string {
  return TIPO_LABEL[tipo]
}

export function buildClosedScope(input: {
  formData: Record<string, unknown>
  snapshot?: FinanceProposalSnapshotRow | null
  includeValues: boolean
}): ClosedScopeViewModel {
  const { formData, snapshot, includeValues } = input

  let lines: ClosedScopeLine[] = []
  let observacoes =
    typeof formData.description === 'string' && formData.description.trim()
      ? formData.description.trim()
      : null
  let versao: number | null = null
  let createdAt: string | null = null
  let proposalPublicUrl: string | null = null

  if (snapshot) {
    versao = snapshot.versao ?? null
    createdAt = snapshot.createdAt ?? null
    const parsed = parseFinanceProposalSnapshot(snapshot)
    lines = parsed.items.map((item) => ({
      id: item.id,
      descricao: item.descricao,
      tipo: item.tipo,
      quantidade: item.quantidade,
      ...(includeValues
        ? { valorUnitario: item.valorUnitario, total: item.total }
        : {}),
    }))
    if (!observacoes && parsed.meta.observacoes) {
      observacoes = parsed.meta.observacoes
    }

    const proposalId = snapshot.propostaId ?? extractPublicProposalId(snapshot.pdfUrl)
    if (proposalId) {
      proposalPublicUrl = publicProposalPreviewPath(proposalId, 'v2')
    } else if (typeof parsed.pdfUrl === 'string' && parsed.pdfUrl) {
      proposalPublicUrl = parsed.pdfUrl
    }
  }

  const servicosRaw = formData.servicosContratados
  const servicosText =
    typeof servicosRaw === 'string' && servicosRaw.trim() ? servicosRaw.trim() : null

  const plataformaRaw = formData.plataforma
  const plataforma =
    typeof plataformaRaw === 'string' && plataformaRaw.trim() ? plataformaRaw.trim() : null

  return {
    lines,
    servicosText,
    plataforma,
    observacoes,
    proposalPublicUrl,
    versao,
    createdAt,
  }
}

export function hasClosedScopeContent(scope: ClosedScopeViewModel): boolean {
  return (
    scope.lines.length > 0 ||
    Boolean(scope.servicosText) ||
    Boolean(scope.plataforma) ||
    Boolean(scope.observacoes)
  )
}

export function buildClosedScopePlanViews(
  snapshot?: FinanceProposalSnapshotRow | null,
): PublicPlanView[] {
  if (!snapshot) return []
  const parsed = parseFinanceProposalSnapshot(snapshot)
  return buildPublicPlanViews(parsed.items)
}
